import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, FileText, Search, Download, Play, Users, Mail, MailCheck, Loader2 as LoaderIcon, History, Eye } from 'lucide-react';
import api from '@/lib/axios';
import { footerToParams, getFooterSettings } from '@/components/FooterDateBar';
import { formatApiError } from '@/lib/errors';
import { toast } from 'sonner';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { formatClocks, formatDate, formatDeNumber, payerNameLabel } from '@/lib/utils';
import UrlFilterChip from '@/components/UrlFilterChip';
import DispatchLogDialog from './DispatchLogDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DateInput from '@/components/ui/date-input';
import DocumentEmailDialog from '@/components/email/DocumentEmailDialog';
import BulkEmailRecipientsDialog from '@/components/email/BulkEmailRecipientsDialog';
import useTableSort from '@/hooks/useTableSort';
import SortableTh from '@/components/table/SortableTh';

// Schlüssel müssen zu `sortable` in backend/routers/invoices.py passen.
const MONATSNAMEN = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

function monateDesJahres(bezug = new Date()) {
  const jahr = bezug.getFullYear();
  return MONATSNAMEN.map((name, index) => ({
    value: `${jahr}-${String(index + 1).padStart(2, '0')}-01`,
    label: `${name} ${jahr}`,
  }));
}

const INVOICE_COLUMNS = [
  { key: 'invoice_number', label: 'Rechnungsnr.' },
  { key: 'invoice_type', label: 'Typ' },
  { key: 'recipient', label: 'Empfänger' },
  { key: 'invoice_date', label: 'Datum' },
  { key: 'due_date', label: 'Fällig' },
  { key: 'total_amount', label: 'Betrag' },
  { key: 'payment_status', label: 'Zahlungsstatus' },
];

async function fetchInvoicePdfBlob(invoiceId) {
  const response = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
  return new Blob([response.data], { type: 'application/pdf' });
}

export default function InvoicesList() {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  const [familyBillingDialogOpen, setFamilyBillingDialogOpen] = useState(false);
  const [billingMonth, setBillingMonth] = useState('');
  const [runningBilling, setRunningBilling] = useState(false);
  const [probe, setProbe] = useState(null);
  const [probeFam, setProbeFam] = useState(null);
  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [familyPreview, setFamilyPreview] = useState(null);
  const navigate = useNavigate();
  const [footerTick, setFooterTick] = useState(0);
  useEffect(() => {
    const onFooter = () => setFooterTick((n) => n + 1);
    window.addEventListener('footer-settings-changed', onFooter);
    return () => window.removeEventListener('footer-settings-changed', onFooter);
  }, []);

  // Serverseitige Sortierung: die API liefert nur einen Ausschnitt, im Browser
  // ließe sich sonst bloß die geladene Seite sortieren.
  const { sort, toggleSort } = useTableSort({}, { key: 'invoice_date', dir: 'desc' });

  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);

  const [searchParams] = useSearchParams();
  const guardianId = searchParams.get('guardian_id') || '';
  const [payerLabel, setPayerLabel] = useState('');

  const loadInvoices = useCallback(async () => {
    try {
      const period = footerToParams(getFooterSettings());
      const response = await api.get('/invoices', {
        params: {
          sort_by: sort.key,
          sort_dir: sort.dir,
          ...(query ? { q: query } : {}),
          ...(guardianId ? { guardian_id: guardianId } : {}),
          ...(period.period_start ? { period_start: period.period_start } : {}),
          ...(period.period_end ? { period_end: period.period_end } : {}),
        },
      });
      setInvoices(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Rechnungen');
    } finally {
      setLoading(false);
    }
  }, [sort, query, footerTick, guardianId]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    loadStats();
  }, [guardianId]);

  useEffect(() => {
    if (!guardianId) {
      setPayerLabel('');
      return undefined;
    }
    let gone = false;
    api.get(`/guardians/${guardianId}`)
      .then((response) => {
        if (!gone) setPayerLabel(payerNameLabel(response.data?.last_name, response.data?.first_name));
      })
      .catch(() => {
        if (!gone) setPayerLabel('');
      });
    return () => { gone = true; };
  }, [guardianId]);

  const loadStats = async () => {
    try {
      const response = await api.get('/invoices/stats/summary', {
        params: guardianId ? { guardian_id: guardianId } : {},
      });
      setStats(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Statistiken:', error);
    }
  };

  const loadFamilies = async () => {
    try {
      const response = await api.get('/families?per_page=100');
      // Only show families with 2+ members
      setFamilies(response.data.items.filter(f => f.member_count >= 2));
    } catch (error) {
      console.error('Error loading families:', error);
    }
  };

  const openFamilyBillingDialog = () => {
    loadFamilies();
    setSelectedFamily(null);
    setFamilyPreview(null);
    setBillingMonth(new Date().toISOString().slice(0, 7) + '-01');
    setFamilyBillingDialogOpen(true);
  };

  const handleFamilySelect = async (familyId) => {
    setSelectedFamily(familyId);
    setFamilyPreview(null);
    
    if (familyId && billingMonth) {
      try {
        const response = await api.get(`/invoices/family/${familyId}/preview?billing_month=${billingMonth}`);
        setFamilyPreview(response.data);
      } catch (error) {
        toast.error('Fehler beim Laden der Vorschau');
      }
    }
  };

  const createFamilyInvoice = async () => {
    if (!selectedFamily || !billingMonth) return;
    
    setRunningBilling(true);
    try {
      const response = await api.post(`/invoices/family?family_id=${selectedFamily}&billing_month=${billingMonth}`);
      toast.success(`Sammelrechnung ${response.data.invoice_number} erstellt (${response.data.total_amount} €)`);
      setFamilyBillingDialogOpen(false);
      loadInvoices();
      loadStats();
    } catch (error) {
      toast.error(formatApiError(error, 'Fehler beim Erstellen'));
    } finally {
      setRunningBilling(false);
    }
  };

  const runFamilyBillingProbe = async () => {
    if (!billingMonth) return toast.error('Bitte Abrechnungsmonat auswählen');
    setRunningBilling(true);
    try {
      const bm = billingMonth.length === 7 ? `${billingMonth}-01` : billingMonth;
      const response = await api.post(`/invoices/family-billing-run?billing_month=${bm}&dry_run=true`);
      setProbeFam(response.data);
    } catch (error) {
      toast.error('Probelauf fehlgeschlagen');
    } finally {
      setRunningBilling(false);
    }
  };

  const runFamilyBillingRun = async () => {
    if (!billingMonth) return;
    
    setRunningBilling(true);
    try {
      const bm = billingMonth.length === 7 ? `${billingMonth}-01` : billingMonth;
      const response = await api.post(`/invoices/family-billing-run?billing_month=${bm}`);
      toast.success(`${response.data.created_invoices} Sammelrechnungen erstellt (${formatDeNumber(response.data.total_amount)} €)`);
      setFamilyBillingDialogOpen(false);
      setProbeFam(null);
      loadInvoices();
      loadStats();
    } catch (error) {
      toast.error('Fehler beim Abrechnungslauf');
    } finally {
      setRunningBilling(false);
    }
  };

  const runBillingProbe = async () => {
    if (!billingMonth) return toast.error('Bitte Abrechnungsmonat auswählen');
    setRunningBilling(true);
    try {
      const bm = billingMonth.length === 7 ? `${billingMonth}-01` : billingMonth;
      const response = await api.post('/invoices/billing-run', { billing_month: bm, dry_run: true });
      setProbe(response.data);
    } catch (error) {
      toast.error(formatApiError(error, 'Probelauf fehlgeschlagen'));
    } finally {
      setRunningBilling(false);
    }
  };

  const handleBillingRun = async () => {
    if (!billingMonth) {
      toast.error('Bitte Abrechnungsmonat auswählen');
      return;
    }

    setRunningBilling(true);
    try {
      const bm = billingMonth.length === 7 ? `${billingMonth}-01` : billingMonth;
      const response = await api.post('/invoices/billing-run', {
        billing_month: bm
      });
      toast.success(`${response.data.created_invoices} Rechnungen erstellt!`);
      setBillingDialogOpen(false);
      setBillingMonth('');
      setProbe(null);
      loadInvoices();
      loadStats();
    } catch (error) {
      toast.error(formatApiError(error, 'Fehler beim Abrechnungslauf'));
    } finally {
      setRunningBilling(false);
    }
  };

  const [sendingId, setSendingId] = useState(null);
  const [dispatching, setDispatching] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [pdfView, setPdfView] = useState(null); // { url, title }
  const [sendPreview, setSendPreview] = useState(null); // { invoice, url, recipient, previewToken }
  const [previewBusyId, setPreviewBusyId] = useState(null);
  const [dispatchPreview, setDispatchPreview] = useState(null);

  const closePdfView = () => {
    if (pdfView?.url) URL.revokeObjectURL(pdfView.url);
    setPdfView(null);
  };

  const closeSendPreview = () => {
    if (sendPreview?.url) URL.revokeObjectURL(sendPreview.url);
    setSendPreview(null);
  };

  const handlePreviewPDF = async (invoice) => {
    setPreviewBusyId(invoice.id);
    try {
      const blob = await fetchInvoicePdfBlob(invoice.id);
      const url = URL.createObjectURL(blob);
      setPdfView({ url, title: `Rechnung ${invoice.invoice_number}` });
    } catch (error) {
      toast.error(formatApiError(error, 'PDF-Voransicht fehlgeschlagen'));
    } finally {
      setPreviewBusyId(null);
    }
  };

  const openSendPreview = async (invoice) => {
    setPreviewBusyId(invoice.id);
    try {
      const blob = await fetchInvoicePdfBlob(invoice.id);
      const url = URL.createObjectURL(blob);
      let recipient = invoice.sent_to || null;
      let previewToken = null;
      try {
        const r = await api.get(`/invoices/${invoice.id}/send-preview`);
        recipient = r.data?.recipient || recipient;
        previewToken = r.data?.preview_token || null;
      } catch {
        /* Empfänger optional – Versand prüft serverseitig */
      }
      setSendPreview({ invoice, url, recipient, previewToken });
    } catch (error) {
      toast.error(formatApiError(error, 'Voransicht konnte nicht geladen werden'));
    } finally {
      setPreviewBusyId(null);
    }
  };

  const handleSendEmail = async (routing = {}) => {
    if (!sendPreview?.invoice) return;
    if (!sendPreview.previewToken) {
      toast.error('Voransicht unvollständig — bitte erneut öffnen');
      return;
    }
    const invoice = sendPreview.invoice;
    setSendingId(invoice.id);
    try {
      const r = await api.post(`/invoices/${invoice.id}/send-email`, {
        preview_token: sendPreview.previewToken,
        ...routing,
      });
      if (r.data.queued) {
        toast.info(`Rechnung ${invoice.invoice_number} zur Freigabe eingereiht — Versand erst nach Freigabe in der E-Mail-Queue`);
      } else {
        toast.success(`Rechnung ${invoice.invoice_number} an ${r.data.recipient} gesendet`);
      }
      closeSendPreview();
      loadInvoices();
    } catch (error) {
      toast.error(formatApiError(error, 'Versand fehlgeschlagen'));
    } finally {
      setSendingId(null);
    }
  };

  const openDispatchPreview = async () => {
    setDispatchPreview({ loading: true, recipients: [], skipped: [] });
    try {
      const r = await api.get('/invoices/dispatch-preview');
      setDispatchPreview({
        loading: false,
        recipients: r.data.recipients || [],
        skipped: r.data.skipped || [],
      });
    } catch (error) {
      setDispatchPreview(null);
      toast.error(formatApiError(error, 'Empfängerliste konnte nicht geladen werden'));
    }
  };

  const handleDispatchRun = async (routing = {}) => {
    setDispatching(true);
    try {
      const r = await api.post('/invoices/dispatch-run', { preview_confirmed: true, ...routing });
      const d = r.data;
      toast.success(`Versand: ${d.sent} gesendet, ${d.queued || 0} zur Freigabe eingereiht, ${d.no_email} ohne E-Mail, ${d.failed} fehlgeschlagen (${d.candidates} offen)`);
      if (d.errors?.length) toast.error(d.errors.join(' · '));
      setDispatchPreview(null);
      loadInvoices();
    } catch (error) {
      toast.error(formatApiError(error, 'Versand fehlgeschlagen'));
    } finally {
      setDispatching(false);
    }
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rechnung_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Download');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: { label: 'Offen', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      paid: { label: 'Bezahlt', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      cancelled: { label: 'Storniert', className: 'bg-slate-100 text-slate-700' },
    };
    return <Badge className={variants[status]?.className || ''}>{variants[status]?.label || status}</Badge>;
  };

  const getPaymentStatusBadge = (status) => {
    const variants = {
      unpaid: { label: 'Unbezahlt', className: 'bg-rose-50 text-rose-700 border-rose-200' },
      partial: { label: 'Teilzahlung', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      paid: { label: 'Bezahlt', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    };
    return <Badge className={variants[status]?.className || ''}>{variants[status]?.label || status}</Badge>;
  };

  const [statFilter, setStatFilter] = useState('all'); // 'all' | 'open' | 'paid' | 'overdue'

  // Honor `?status=open|paid|overdue` deep links from dashboards
  useEffect(() => {
    const s = searchParams.get('status');
    if (s && ['open', 'paid', 'overdue'].includes(s)) {
      setStatFilter(s);
    }
  }, [searchParams]);

  const filteredInvoices = invoices.filter(inv => {
    if (statFilter === 'open') return inv.payment_status === 'unpaid' && inv.status === 'open';
    if (statFilter === 'paid') return inv.payment_status === 'paid';
    if (statFilter === 'overdue') {
      if (inv.payment_status === 'paid') return false;
      const due = inv.due_date ? new Date(inv.due_date) : null;
      return due && due < new Date();
    }
    return true;
  });

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" data-testid="invoices-title">
              Rechnungen
            </h1>
            <p className="text-slate-600">Alle Rechnungen verwalten</p>
            <UrlFilterChip
              params={[
                {
                  key: 'status',
                  label: 'Zahlungsstatus',
                  valueLabels: { open: 'Offen', paid: 'Bezahlt', overdue: 'Überfällig' },
                },
                {
                  key: 'guardian_id',
                  label: 'Zahlungspflichtiger',
                  valueLabels: payerLabel ? { [guardianId]: payerLabel } : {},
                },
              ]}
              onReset={() => setStatFilter('all')}
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setLogOpen(true)}
              variant="ghost"
              data-testid="dispatch-log-button"
              className="text-slate-600"
            >
              <History className="w-4 h-4 mr-2" />
              Versandprotokoll
            </Button>
            <Button
              onClick={openDispatchPreview}
              disabled={dispatching}
              variant="outline"
              data-testid="dispatch-run-button"
              className="border-sky-300 text-sky-700 hover:bg-sky-50"
            >
              {dispatching ? <LoaderIcon className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Offene per E-Mail versenden
            </Button>
            <Button
              onClick={openFamilyBillingDialog}
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <Users className="w-4 h-4 mr-2" />
              Sammelrechnung
            </Button>
            <Dialog open={billingDialogOpen} onOpenChange={(o) => {
              setBillingDialogOpen(o);
              if (o) {
                const monate = monateDesJahres();
                setBillingMonth((aktuell) => (
                  monate.some((monat) => monat.value === aktuell) ? aktuell : monate[new Date().getMonth()].value
                ));
              } else {
                setProbe(null);
              }
            }}>
              <DialogTrigger asChild>
                <Button
                  data-testid="billing-run-button"
                  className="bg-brand-red text-white hover:bg-brand-red/90"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Abrechnungslauf
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Monatlicher Abrechnungslauf</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="billing-month">Abrechnungsmonat</Label>
                    <select
                      id="billing-month"
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      value={billingMonth}
                      onChange={(e) => { setBillingMonth(e.target.value); setProbe(null); }}
                    >
                      {monateDesJahres().map((monat) => (
                        <option key={monat.value} value={monat.value}>{monat.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      Berechnet die Belegungen der Schüler dieser Schule
                    </p>
                  </div>
                  {probe && (
                    <div className="border border-amber-200 bg-amber-50/50 rounded-lg" data-testid="billing-probe-result">
                      <div className="px-4 py-2 border-b border-amber-200 flex items-center justify-between text-sm">
                        <span className="font-semibold text-amber-800">
                          Probelauf: {probe.created_invoices} Rechnungen würden erstellt · {formatDeNumber(probe.total_amount)} €
                        </span>
                        <span className="text-xs text-amber-700">{probe.skipped_contracts} übersprungen</span>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-amber-100/60 text-amber-900 sticky top-0">
                            <tr>
                              <th className="text-left px-3 py-1.5">Empfänger</th>
                              <th className="text-left px-3 py-1.5">Schüler:in</th>
                              <th className="text-left px-3 py-1.5">Grundlage</th>
                              <th className="text-right px-3 py-1.5">Rabatt</th>
                              <th className="text-right px-3 py-1.5">Betrag</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(probe.previews || []).map((p, i) => (
                              <tr key={i} className="border-t border-amber-100">
                                <td className="px-3 py-1">{p.recipient}</td>
                                <td className="px-3 py-1 text-slate-500">{p.student_name}</td>
                                <td className="px-3 py-1 font-mono text-[10px]">{p.contract_number}</td>
                                <td className="px-3 py-1 text-right">{p.discount_amount > 0 ? `-${formatDeNumber(p.discount_amount)} €` : '—'}</td>
                                <td className="px-3 py-1 text-right font-semibold">{formatDeNumber(p.total_amount)} €</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="px-4 py-2 text-[11px] text-amber-700 border-t border-amber-200">
                        Es wurden keine Rechnungen oder PDFs erzeugt.
                      </p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      onClick={runBillingProbe}
                      disabled={runningBilling}
                      variant="outline"
                      className="flex-1 border-amber-300 text-amber-700 hover:bg-brand-red/5"
                      data-testid="billing-probe-btn"
                    >
                      {runningBilling ? 'Berechne...' : 'Probelauf (Vorschau)'}
                    </Button>
                    <Button
                      onClick={handleBillingRun}
                      disabled={runningBilling}
                      className="flex-1"
                      data-testid="billing-run-confirm-btn"
                    >
                      {runningBilling ? 'Wird erstellt...' : 'Abrechnungslauf starten'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Family Invoice Dialog */}
        <Dialog open={familyBillingDialogOpen} onOpenChange={(o) => { setFamilyBillingDialogOpen(o); if (!o) setProbeFam(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Sammelrechnung erstellen</DialogTitle>
              <DialogDescription>
                Erstellen Sie eine Rechnung für alle Kinder einer Familie.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Abrechnungsmonat</Label>
                <DateInput
                  
                  value={billingMonth}
                  onChange={(e) => {
                    setBillingMonth(e.target.value);
                    if (selectedFamily) handleFamilySelect(selectedFamily);
                  }}
                />
              </div>
              <div>
                <Label>Familie</Label>
                <Select value={selectedFamily || ''} onValueChange={handleFamilySelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Familie auswählen..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {families.map((family) => (
                      <SelectItem key={family.id} value={family.id}>
                        {family.name} ({family.member_count} Mitglieder)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Preview */}
              {familyPreview && (
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Familie:</span>
                    <span className="font-medium">{familyPreview.family_name}</span>
                  </div>
                  <div className="border-t pt-2">
                    {familyPreview.members?.map((member, idx) => (
                      <div key={idx} className="mb-2">
                        <div className="font-medium text-sm">{member.student_name}</div>
                        {member.items?.map((item, iidx) => (
                          <div key={iidx} className="flex justify-between text-xs text-slate-600 pl-3">
                            <span>{formatClocks(item.description) || item.description}</span>
                            <span>{item.amount != null ? `${formatDeNumber(item.amount)} €` : ''}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-2 flex justify-between font-bold">
                    <span>Gesamt:</span>
                    <span>{familyPreview.total_amount != null ? `${formatDeNumber(familyPreview.total_amount)} €` : ''}</span>
                  </div>
                </div>
              )}
              
              {familyPreview && familyPreview.total_amount === 0 && (
                <div className="text-sm text-brand-red bg-amber-50 p-3 rounded">
                  Diese Familie hat keine abrechenbaren Positionen für den gewählten Monat.
                </div>
              )}

              {probeFam && (
                <div className="border border-amber-200 bg-amber-50/50 rounded-lg" data-testid="family-probe-result">
                  <div className="px-4 py-2 border-b border-amber-200 text-sm font-semibold text-amber-800">
                    Probelauf: {probeFam.created_invoices} Sammelrechnungen · {formatDeNumber(probeFam.total_amount)} € · {probeFam.skipped_families} übersprungen
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-amber-100/60 text-amber-900 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-1.5">Familie</th>
                          <th className="text-left px-3 py-1.5">Empfänger</th>
                          <th className="text-right px-3 py-1.5">Positionen</th>
                          <th className="text-right px-3 py-1.5">Betrag</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(probeFam.previews || []).map((p, i) => (
                          <tr key={i} className="border-t border-amber-100">
                            <td className="px-3 py-1">{p.family_name}</td>
                            <td className="px-3 py-1 text-slate-500">{p.recipient}</td>
                            <td className="px-3 py-1 text-right">{p.item_count}</td>
                            <td className="px-3 py-1 text-right font-semibold">{formatDeNumber(p.total_amount)} €</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="px-4 py-2 text-[11px] text-amber-700 border-t border-amber-200">
                    Es wurden keine Rechnungen oder PDFs erzeugt.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter className="gap-2 mt-4">
              <Button variant="outline" onClick={runFamilyBillingProbe} disabled={runningBilling}
                      className="border-amber-300 text-amber-700 hover:bg-brand-red/5"
                      data-testid="family-probe-btn">
                Probelauf (alle Familien)
              </Button>
              <Button variant="outline" onClick={runFamilyBillingRun} disabled={runningBilling}>
                Alle Familien abrechnen
              </Button>
              <Button 
                onClick={createFamilyInvoice} 
                disabled={runningBilling || !selectedFamily || !familyPreview || familyPreview.total_amount === 0}
              >
                {runningBilling ? 'Erstelle...' : 'Sammelrechnung erstellen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <button
              type="button"
              onClick={() => setStatFilter('all')}
              className={`bg-white rounded-xl border shadow-sm p-6 text-left transition-all ${statFilter === 'all' ? 'ring-2 ring-slate-500 border-slate-500' : 'border-slate-200 hover:shadow-md'}`}
              data-testid="filter-card-total"
            >
              <p className="text-sm font-medium text-slate-500 mb-1">Gesamt</p>
              <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
            </button>
            <button
              type="button"
              onClick={() => setStatFilter(statFilter === 'open' ? 'all' : 'open')}
              className={`bg-white rounded-xl border shadow-sm p-6 text-left transition-all ${statFilter === 'open' ? 'ring-2 ring-brand-red border-brand-red' : 'border-slate-200 hover:shadow-md'}`}
              data-testid="filter-card-open"
            >
              <p className="text-sm font-medium text-slate-500 mb-1">Offen</p>
              <p className="text-3xl font-bold text-brand-red">{stats.open}</p>
            </button>
            <button
              type="button"
              onClick={() => setStatFilter(statFilter === 'paid' ? 'all' : 'paid')}
              className={`bg-white rounded-xl border shadow-sm p-6 text-left transition-all ${statFilter === 'paid' ? 'ring-2 ring-emerald-500 border-emerald-500' : 'border-slate-200 hover:shadow-md'}`}
              data-testid="filter-card-paid"
            >
              <p className="text-sm font-medium text-slate-500 mb-1">Bezahlt</p>
              <p className="text-3xl font-bold text-emerald-600">{stats.paid}</p>
            </button>
            <button
              type="button"
              onClick={() => setStatFilter(statFilter === 'overdue' ? 'all' : 'overdue')}
              className={`bg-white rounded-xl border shadow-sm p-6 text-left transition-all ${statFilter === 'overdue' ? 'ring-2 ring-brand-red border-brand-red' : 'border-slate-200 hover:shadow-md'}`}
              data-testid="filter-card-overdue"
            >
              <p className="text-sm font-medium text-slate-500 mb-1">Überfällig</p>
              <p className="text-3xl font-bold text-brand-red">{stats.overdue}</p>
            </button>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-4 border-b border-slate-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="search-invoices-input"
                placeholder="Alles durchsuchen…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="invoices-table">
              <thead className="bg-slate-50 text-slate-500 font-medium text-xs uppercase tracking-wider border-b border-slate-200">
                <tr>
                  {INVOICE_COLUMNS.map((col) => (
                    <SortableTh
                      key={col.key}
                      sortKey={col.key}
                      sort={sort}
                      onSort={toggleSort}
                      testIdPrefix="invoices-sort"
                    >
                      {col.label}
                    </SortableTh>
                  ))}
                  <th className="p-4 text-left">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-slate-500">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-slate-500">
                      Keine Rechnungen gefunden
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      data-testid={`invoice-row-${invoice.id}`}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <td className="p-4 text-sm font-mono text-slate-700 font-medium">
                        {invoice.invoice_number}
                      </td>
                      <td className="p-4 text-sm">
                        {invoice.invoice_type === 'family' ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            <Users className="w-3 h-3 mr-1" />
                            Sammel
                          </Badge>
                        ) : (
                          <Badge variant="outline">Einzel</Badge>
                        )}
                      </td>
                      <td className="p-4 text-sm text-slate-700">
                        {invoice.family_name || invoice.student_name || 'Unbekannt'}
                      </td>
                      <td className="p-4 text-sm text-slate-600">{formatDate(invoice.invoice_date)}</td>
                      <td className="p-4 text-sm text-slate-600">{formatDate(invoice.due_date)}</td>
                      <td className="p-4 text-sm text-slate-900 font-medium">
                        {formatDeNumber(invoice.total_amount)} €
                      </td>
                      <td className="p-4 text-sm">{getPaymentStatusBadge(invoice.payment_status)}</td>
                      <td className="p-4 text-sm whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePreviewPDF(invoice)}
                          disabled={previewBusyId === invoice.id}
                          title="PDF-Voransicht"
                          data-testid={`preview-invoice-${invoice.id}`}
                        >
                          {previewBusyId === invoice.id && !sendPreview
                            ? <LoaderIcon className="w-4 h-4 animate-spin" />
                            : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadPDF(invoice.id, invoice.invoice_number)}
                          title="PDF herunterladen"
                          data-testid={`download-invoice-${invoice.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title={invoice.sent_at
                            ? `Versendet am ${formatDate(invoice.sent_at)} an ${invoice.sent_to || '—'} — Voransicht vor erneutem Versand`
                            : 'Per E-Mail senden (mit Voransicht)'}
                          onClick={() => openSendPreview(invoice)}
                          disabled={previewBusyId === invoice.id || sendingId === invoice.id}
                          data-testid={`send-invoice-${invoice.id}`}
                          className={invoice.sent_at ? 'text-emerald-600 hover:text-emerald-700' : 'text-slate-600'}
                        >
                          {sendingId === invoice.id
                            ? <LoaderIcon className="w-4 h-4 animate-spin" />
                            : invoice.sent_at ? <MailCheck className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <DispatchLogDialog open={logOpen} onClose={() => setLogOpen(false)} />

      <Dialog open={!!pdfView} onOpenChange={(o) => { if (!o) closePdfView(); }}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col" data-testid="invoice-pdf-view-dialog">
          <DialogHeader>
            <DialogTitle>{pdfView?.title || 'Rechnung'}</DialogTitle>
            <DialogDescription>Nur Voransicht — es wird nichts versendet.</DialogDescription>
          </DialogHeader>
          {pdfView?.url && (
            <iframe src={pdfView.url} title={pdfView.title} className="flex-1 w-full rounded border border-slate-200" data-testid="invoice-pdf-view-iframe" />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closePdfView}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocumentEmailDialog
        open={!!sendPreview}
        onClose={closeSendPreview}
        onConfirm={handleSendEmail}
        confirming={!!sendingId}
        title={sendPreview?.invoice ? `Rechnung per E-Mail — ${sendPreview.invoice.invoice_number}` : 'Rechnung per E-Mail'}
        recipient={sendPreview?.recipient}
        pdfUrl={sendPreview?.url}
        testId="invoice-send-preview-dialog"
        confirmTestId="invoice-send-confirm"
        iframeTitle={`Voransicht ${sendPreview?.invoice?.invoice_number || ''}`}
      />

      <BulkEmailRecipientsDialog
        open={!!dispatchPreview}
        onClose={() => { if (!dispatching) setDispatchPreview(null); }}
        onConfirm={handleDispatchRun}
        confirming={dispatching}
        loading={!!dispatchPreview?.loading}
        title="Offene Rechnungen per E-Mail"
        recipients={dispatchPreview?.recipients || []}
        skipped={dispatchPreview?.skipped || []}
        testId="invoice-dispatch-preview-dialog"
      />
    </Layout>
  );
}
